import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import CKEditorWithSource from "./CKEditorWithSource";
import axios from "axios";
import {
  adminCourseStepSectionCreateAction,
  adminCourseStepSectionGetAction,
  adminCourseStepSectionUpdateAction
} from "../Action/action";
import config from "../coreFIles/config";
import { getAdminAuthToken } from "../coreFIles/auth";

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function buildApiUrl(endpoint) {
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

class CkUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.controller = new AbortController();
  }
  abort() {
    try {
      this.controller.abort();
    } catch (e) {}
  }
  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("upload", file);
    const token = getAdminAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
      headers,
      withCredentials: true,
      signal: this.controller.signal
    });
    const url = resp?.data?.url;
    if (!url) throw new Error("Upload failed");
    return { default: resolvePublicUrl(url) };
  }
}

function CkUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CkUploadAdapter(loader);
}

export default function CourseStepSectionForm({ mode }) {
  const { sectionId } = useParams();
  const navigate = useNavigate();

  const [loadingRow, setLoadingRow] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const [title, setTitle] = useState("");
  const [displayOrder, setDisplayOrder] = useState("0");
  const [isActive, setIsActive] = useState("1");
  const [description, setDescription] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (mode !== "edit") return;
      setLoadingRow(true);
      setError("");
      try {
        const res = await adminCourseStepSectionGetAction(sectionId);
        if (cancelled) return;
        if (!res?.success) {
          setError(res?.message || "Failed to load step section.");
          return;
        }
        const r = res.data || {};
        setTitle(r.title || "");
        const ord = r.display_order;
        setDisplayOrder(
          ord != null && ord !== "" && Number.isFinite(Number(ord)) ? String(Math.max(0, parseInt(String(ord), 10))) : "0"
        );
        setIsActive(Number(r.is_active) === 1 ? "1" : "0");
        setDescription(r.description || "");
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || "Failed to load.");
      } finally {
        if (!cancelled) setLoadingRow(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [sectionId, mode]);

  const validate = () => {
    const errs = {};
    if (!String(title || "").trim()) errs.title = "Title is required.";
    const ordRaw = String(displayOrder ?? "").replace(/\D/g, "");
    const ord = ordRaw === "" ? 0 : parseInt(ordRaw, 10);
    if (!Number.isFinite(ord) || ord < 0) errs.displayOrder = "Display order must be zero or a positive number.";
    return errs;
  };

  const basePath = "/step-sections";

  const onSave = async () => {
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please fix the required fields.", { toastId: "css-form-save" });
      return;
    }

    setSaving(true);
    setError("");
    try {
      const ordStr = String(displayOrder ?? "").replace(/\D/g, "");
      const display_order = ordStr === "" ? 0 : Math.max(0, parseInt(ordStr, 10));
      const body = {
        title: String(title).trim(),
        display_order,
        is_active: isActive === "1" ? 1 : 0,
        description: description || ""
      };
      const res =
        mode === "edit"
          ? await adminCourseStepSectionUpdateAction(sectionId, body)
          : await adminCourseStepSectionCreateAction(body);
      if (!res?.success) throw new Error(res?.message || "Save failed");

      toast.success(mode === "edit" ? "Step section updated" : "Step section created", { toastId: "css-form-save" });
      navigate(basePath);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Save failed";
      setError(msg);
      toast.error(msg, { toastId: "css-form-save" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      {mode === "edit" && loadingRow ? (
        <div className="loading-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="loading-spinner" aria-hidden="true" />
        </div>
      ) : null}

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Could not load/save step section.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to={basePath}>Step sections</Link>
          <span>›</span>
          <span>{mode === "edit" ? "Edit" : "Add"}</span>
        </div>
        <Link to={basePath} className="btn btn-outline btn-sm">
          ← Back
        </Link>
      </div>

      <div className="card mb-6">
        <h2 className="section-title">Step section details</h2>

        <div className="form-group">
          <label>
            Title <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setFieldErrors((p) => ({ ...p, title: "" }));
            }}
            aria-invalid={!!fieldErrors.title}
            style={fieldErrors.title ? { borderColor: "var(--danger)" } : undefined}
          />
          {fieldErrors.title ? (
            <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
              {fieldErrors.title}
            </div>
          ) : null}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Display order</label>
            <input
              type="text"
              inputMode="numeric"
              className="form-control"
              placeholder="0"
              value={displayOrder}
              onInput={(e) => {
                const v = e.target.value.replace(/\D/g, "");
                setDisplayOrder(v);
                setFieldErrors((p) => ({ ...p, displayOrder: "" }));
              }}
              aria-invalid={!!fieldErrors.displayOrder}
              style={fieldErrors.displayOrder ? { borderColor: "var(--danger)" } : undefined}
            />
            {fieldErrors.displayOrder ? (
              <div className="small-label" style={{ marginTop: 6, color: "var(--danger)" }}>
                {fieldErrors.displayOrder}
              </div>
            ) : null}
          </div>
          <div className="form-group">
            <label>Status</label>
            <select className="form-control" value={isActive} onChange={(e) => setIsActive(e.target.value)}>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Description</label>
          <CKEditorWithSource
            heightClassName="ckeditor-height-200"
            containerStyle={{ border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" }}
            config={{ extraPlugins: [CkUploadAdapterPlugin] }}
            data={description}
            onChange={(html) => setDescription(html)}
          />
        </div>

        <div className="flex gap-3" style={{ marginTop: 20 }}>
          <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving || loadingRow}>
            {saving ? "Saving…" : "Save"}
          </button>
          <Link to={basePath} className="btn btn-outline">
            Cancel
          </Link>
        </div>
      </div>
    </div>
  );
}
